import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import styled, { css } from 'styled-components';
import {
    HiOutlineUserCircle,
    HiArrowRightStartOnRectangle
} from 'react-icons/hi2';
import dayjs from 'dayjs';

import theme from '@styles/theme';
import { userVar } from '@store/index';
import { userLogout } from '@modules/authentication';

import { RegularFont, FlexBox } from '@components/styled/StyledComponents';

type MyProfileModalProps = {
    setOpenProfile: Dispatch<SetStateAction<boolean>>;
};

const MyProfileModal = ({ setOpenProfile }: MyProfileModalProps) => {
    const navigate = useNavigate();
    const { latestLogin } = useReactiveVar(userVar);
    const outRef = useRef<HTMLUListElement>(null);

    const goToProfile = () => {
        navigate('/');
        setOpenProfile(false);
    };

    // 백그라운드 스크롤 잠금/해제
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    // 외부 클릭 시 메뉴 닫기
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!outRef?.current?.contains(e.target as HTMLElement)) {
                setOpenProfile(false);
            }
        };
        document.addEventListener('click', handleClickOutside, true);
        return () =>
            document.removeEventListener('click', handleClickOutside, true);
    }, []);

    return (
        <Modal ref={outRef}>
            <Tab
            //  onClick={goToProfile}
            >
                <FlexBox $alignItems="center">
                    <HiOutlineUserCircle size={20} />
                    <RegularFont $margin="0 0 0 12px">내 프로필</RegularFont>
                </FlexBox>
                <RegularFont
                    $margin="6px 0 0"
                    $fontColor={theme.colors.lightGrayFontColor}
                    $fontSize={14}
                >
                    {`최근 로그인: ${dayjs(latestLogin).format(
                        'YYYY-MM-DD HH:mm'
                    )}`}
                </RegularFont>
            </Tab>
            <Tab onClick={userLogout} $cursor>
                <FlexBox $alignItems="center">
                    <HiArrowRightStartOnRectangle
                        size={20}
                        color={theme.colors.activeRed}
                    />
                    <RegularFont
                        $fontColor={theme.colors.activeRed}
                        $margin="0 0 0 12px"
                    >
                        로그아웃
                    </RegularFont>
                </FlexBox>
            </Tab>
        </Modal>
    );
};

export default MyProfileModal;

const Modal = styled.ul`
    width: 252px;
    min-height: 116px;
    position: absolute;
    z-index: 10;
    right: calc((100vw - 1280px) / 2);
    top: 62px;
    border-radius: 4px;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.16);
    background-color: ${theme.colors.whiteColor};
    overflow: hidden;
`;

const Tab = styled.li<{ $cursor?: boolean }>`
    padding: 16px 16px 12px;
    border-bottom: ${theme.colors.ultraLightGrayBorder} 1px solid;

    &:last-child {
        padding: 12px 16px 16px;
        border-bottom: none;
    }

    &:hover {
        background-color: ${theme.colors.lightGrayHoverColor};
    }

    ${({ $cursor }) =>
        $cursor &&
        css`
            cursor: pointer;
        `}
`;
